import { Checkbox, FormControlLabel, MenuItem, Stack, TextField } from '@mui/material';
import { useState } from 'react';

import {
  INVALID_EMAIL,
  INVALID_PHONE,
  INVALID_POSTAL_CODE,
  INVALID_STATE,
  INVALID_WEBSITE,
  isValidEmail,
  isValidPhone,
  isValidPostalCode,
  isValidState,
  isValidWebsite,
} from '../lib/contact';

export type FormValues = Record<string, string | boolean>;
export type FieldErrors = Record<string, string>;

export interface Field {
  name: string;
  label: string;
  required?: boolean;
  type?:
    | 'text'
    | 'number'
    | 'date'
    | 'checkbox'
    | 'select'
    | 'multiline'
    | 'email'
    | 'phone'
    | 'password'
    | 'state'
    | 'postal_code'
    | 'website';
  options?: { value: string; label: string }[];
  helperText?: string;
  defaultValue?: string | boolean;
}

// Every contact form uses these, so the rules and labels are the same everywhere.
export const EMAIL_FIELD: Field = { name: 'email', label: 'Email', type: 'email' };
export const PHONE_FIELD: Field = { name: 'phone', label: 'Phone', type: 'phone' };

/** The same address fields for a property, a business, and whatever comes next. */
export function addressFields({ required = false }: { required?: boolean } = {}): Field[] {
  return [
    { name: 'address_line1', label: 'Address', required },
    { name: 'address_line2', label: 'Address Line 2' },
    { name: 'city', label: 'City', required },
    { name: 'state', label: 'State', required, type: 'state', helperText: 'Two letters, like GA' },
    { name: 'postal_code', label: 'ZIP Code', required, type: 'postal_code' },
  ];
}

/** Text for an optional column: blank stays null, so "no value" isn't stored as an empty string. */
export function optional(values: FormValues, name: string): string | null {
  const value = String(values[name] ?? '').trim();
  return value === '' ? null : value;
}

export function required(values: FormValues, name: string): string {
  return String(values[name] ?? '').trim();
}

export function optionalNumber(values: FormValues, name: string): number | null {
  const value = optional(values, name);
  return value === null ? null : Number(value);
}

export function blankValues(fields: Field[]): FormValues {
  return Object.fromEntries(
    fields.map((f) => [f.name, f.defaultValue ?? (f.type === 'checkbox' ? false : '')]),
  );
}

/** Messages for the fields that are wrong, so people fix them before anything is sent. */
export function validateFields(fields: Field[], values: FormValues): FieldErrors {
  const errors: FieldErrors = {};
  for (const f of fields) {
    const value = optional(values, f.name);
    if (value === null) continue;
    if (f.type === 'email' && !isValidEmail(value)) errors[f.name] = INVALID_EMAIL;
    if (f.type === 'phone' && !isValidPhone(value)) errors[f.name] = INVALID_PHONE;
    if (f.type === 'state' && !isValidState(value)) errors[f.name] = INVALID_STATE;
    if (f.type === 'postal_code' && !isValidPostalCode(value)) errors[f.name] = INVALID_POSTAL_CODE;
    if (f.type === 'website' && !isValidWebsite(value)) errors[f.name] = INVALID_WEBSITE;
  }
  return errors;
}

/** Values and errors for a form that isn't in a RecordPage. ``validate`` shows any errors. */
export function useFormValues(fields: Field[], initial: FormValues) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  return {
    values,
    setValues,
    errors,
    onChange: (name: string, value: string | boolean) =>
      setValues((current) => ({ ...current, [name]: value })),
    validate: () => {
      const found = validateFields(fields, values);
      setErrors(found);
      return Object.keys(found).length === 0;
    },
  };
}

const INPUT_TYPES: Partial<Record<NonNullable<Field['type']>, string>> = {
  number: 'number',
  date: 'date',
  phone: 'tel',
  password: 'password',
};

export function FormFields({
  fields,
  values,
  errors,
  onChange,
}: {
  fields: Field[];
  values: FormValues;
  errors: FieldErrors;
  onChange: (name: string, value: string | boolean) => void;
}) {
  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {fields.map((f) =>
        f.type === 'checkbox' ? (
          <FormControlLabel
            key={f.name}
            label={f.label}
            control={
              <Checkbox
                checked={Boolean(values[f.name])}
                onChange={(e) => onChange(f.name, e.target.checked)}
              />
            }
          />
        ) : (
          <TextField
            key={f.name}
            label={f.label}
            required={f.required}
            error={f.name in errors}
            helperText={errors[f.name] ?? f.helperText}
            select={f.type === 'select'}
            multiline={f.type === 'multiline'}
            minRows={f.type === 'multiline' ? 3 : undefined}
            type={INPUT_TYPES[f.type ?? 'text'] ?? 'text'}
            slotProps={{
              inputLabel: f.type === 'date' ? { shrink: true } : undefined,
              htmlInput: {
                ...(f.type === 'number' ? { step: 'any', min: 0 } : {}),
                // Not type="email": the browser's popup would block validateFields' clearer message.
                ...(f.type === 'email' ? { inputMode: 'email' as const } : {}),
              },
            }}
            value={String(values[f.name] ?? '')}
            onChange={(e) => onChange(f.name, e.target.value)}
          >
            {f.options?.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      )}
    </Stack>
  );
}
